import { Link } from "react-router";
import { abasBentoGatilho, abasBentoLista } from "../bento/ui";
import {
  Trophy,
  Calendar,
  ArrowUpRight,
  Target,
  Medal,
  Lock,
  CheckCircle2,
  Users,
  Share2,
  Plane,
} from "lucide-react";
import { useMascara } from "../valores-ocultos/contexto";
import { BotaoOlhinho } from "../valores-ocultos/ValoresOcultos";

// Peças visuais compartilhadas entre a lista de campanhas e o detalhe. Ficam
// aqui (e não em cada página) porque o card da lista e os cards de "níveis" do
// detalhe usam o mesmo vocabulário: barra de meta, header colorido, cadeado.

export const ACCENT = "#354660";

/** Bege/dourado da marca. Marca o que é do TIME, não da campanha. */
/**
 * Bege/dourado da paleta oficial da North.
 *
 * (O comentário antigo aqui dizia "azul accent da North" — copiado da linha do
 * ACCENT e nunca corrigido. É o bege, não o azul.)
 */
export const DOURADO = "#c4ae90";

/**
 * ── AS FAIXAS DE CABEÇALHO DOS CARDS ────────────────────────────────────────
 *
 * As DUAS cores da paleta, cheias — sem tinta, sem alpha. A cor diz de que
 * assunto o card é: dourado = time, azul = pessoal.
 *
 * (Tentei antes versões clareadas das duas, casadas na mesma luz, pra que
 * nenhuma parecesse mais importante. Ficou lavado; a paleta cheia é o pedido.)
 *
 * CONSEQUÊNCIA que anda junto: o #354660 tem luz 29%, então texto escuro em
 * cima dele é ilegível. Todo cabeçalho azul usa texto branco — é o que
 * `TEXTO_SOBRE` resolve, e por isso a cor da faixa e a do texto nunca são
 * escolhidas separadamente.
 */
export const FAIXA_TIME = DOURADO;   // #c4ae90
export const FAIXA_PESSOAL = ACCENT; // #354660

/** Cinza claro da paleta oficial — é também o fundo da página. */
export const CINZA = "#c6c6c6";

/**
 * Meta North é a casa falando, não o time nem a pessoa — e ganhou a terceira
 * cor da paleta em vez de disputar o dourado com o "Objetivo da equipe", que
 * fica logo ao lado no mesmo card. Duas coisas diferentes na mesma cor era o
 * que embaralhava a leitura.
 *
 * É de propósito que seja a cor do FUNDO da página: a meta não compete por
 * atenção com a produção, ela emoldura. Quem faz o card não sumir é o corpo
 * branco embaixo da faixa — e o brilho, que só a Meta North tem.
 */
export const FAIXA_CASA = CINZA;

/** Cor de texto legível sobre cada faixa. Ver o comentário acima. */
export const TEXTO_SOBRE = (faixa: string) =>
  faixa.toLowerCase() === ACCENT.toLowerCase() ? "#FFFFFF" : "#232323";

export const TRILHO = "#D9DCE1";

/**
 * Abas no controle segmentado do bento (trilho cinza, pastilha branca) — as
 * classes vêm do kit único (bento/ui) pra Campanhas e Insights não manterem um
 * segundo vocabulário de abas. O estilo "underline" do design de referência
 * Azos saiu quando o bento virou a linguagem do app inteiro (2026-08-25); os
 * cards/heroes das campanhas continuam no modelo aprovado.
 */
export const tabsListClass = abasBentoLista;
export const tabsTriggerClass = `flex-none ${abasBentoGatilho}`;

/** Variante pra poucas abas: cada uma ocupa uma fatia igual da largura. */
export const tabsListDivididaClass =
  'grid w-full h-auto gap-0 rounded-none border-b border-border bg-transparent p-0';
export const tabsTriggerDivididoClass = `w-full justify-center ${abasBentoGatilho}`;

const ICONS: Record<string, typeof Trophy> = {
  trophy: Trophy,
  target: Target,
  users: Users,
  share: Share2,
  plane: Plane,
  medal: Medal,
};

export const campaignIcon = (icon: string | null | undefined) =>
  ICONS[(icon ?? "").toLowerCase()] ?? Trophy;

export const fmtDateBr = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Barra de meta com rótulos nas pontas (piso à esquerda, alvo à direita) —
 * o padrão visual da referência. `value`/`max` são números crus; a formatação
 * dos rótulos fica com quem chama (R$, %, etc).
 *
 * Os rótulos passam pelo olhinho aqui mesmo (em Campanhas viram "R$ ••••",
 * "••••%"); o preenchimento da barra fica — é forma, não número.
 */
export const MetaBar = ({
  pct,
  leftLabel,
  rightLabel,
  title,
  done = false,
  marcos,
}: {
  pct: number;
  leftLabel: string;
  rightLabel: string;
  title?: string;
  done?: boolean;
  /** Posições (0–100) das faixas da campanha. Sem elas a barra é lisa. */
  marcos?: number[];
}) => {
  const m = useMascara();
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="space-y-2">
      {title && (
        <div className="flex items-center gap-1.5 text-sm font-medium">
          {done && <CheckCircle2 className="h-4 w-4" style={{ color: ACCENT }} />}
          <span>{title}</span>
        </div>
      )}
      {/*
        A trilha tem cor própria (e não bg-muted) porque dentro do card branco
        o muted some: em 0% não aparecia barra nenhuma, e os rótulos 0%/100%
        ficavam soltos, sem dar pra entender que ali era progresso.
        O preenchimento é sempre o azul da North — meta batida muda o ícone.
      */}
      <div className="relative h-2 w-full rounded-full" style={{ backgroundColor: TRILHO }}>
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${clamped}%`, backgroundColor: ACCENT }}
        />
        {/* Marcos das faixas. Sem eles a barra diz só "quanto andei"; com eles
            diz "quanto falta pra PRÓXIMA faixa", que é o que faz alguém correr.
            Cheio quando já passou, vazio quando ainda não. */}
        {marcos?.map((m) => {
          const pos = Math.max(0, Math.min(100, m));
          const atingido = clamped >= pos;
          return (
            <span
              key={pos}
              className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-colors"
              style={{
                left: `${pos}%`,
                backgroundColor: atingido ? ACCENT : "#FFFFFF",
                borderColor: atingido ? ACCENT : TRILHO,
              }}
            />
          );
        })}
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{m(leftLabel)}</span>
        <span>{m(rightLabel)}</span>
      </div>
    </div>
  );
};

/**
 * Card da lista de campanhas: faixa de título cinza com o ícone em badge
 * circular, corpo com a descrição + barra de andamento e rodapé com a vigência.
 * `progress` é opcional — campanhas sem métrica apurável saem sem barra.
 */
export const CampanhaCard = ({
  to,
  name,
  description,
  icon,
  vigencia,
  progress,
  badge,
}: {
  to: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  vigencia: string;
  progress?: { pct: number; leftLabel: string; rightLabel: string; marcos?: number[] } | null;
  badge?: string | null;
}) => {
  const Icon = campaignIcon(icon);
  return (
    <Link
      to={to}
      className="group flex flex-col overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-black/5
                 transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      {/* Azul da North, não o cinza de antes. O CampanhaCard só aparece em
          "Minha performance", e a faixa é o que diz de que assunto o card é:
          azul = pessoal, dourado = time (ver ResumoCard). Cinza não dizia
          nada, e os dois blocos da página ficavam indistinguíveis. */}
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        style={{ backgroundColor: FAIXA_PESSOAL, color: TEXTO_SOBRE(FAIXA_PESSOAL) }}
      >
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{name}</div>
          {badge && (
            <span className="mt-1 inline-flex items-center rounded-[4px] bg-alerta-fundo px-2 py-0.5 text-[10px] font-semibold text-alerta">
              {badge}
            </span>
          )}
        </div>
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${ACCENT}1a`, color: ACCENT }}
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-4 px-4 py-4">
        <p className="line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
          {description ?? ""}
        </p>
        {progress && (
          <MetaBar
            pct={progress.pct}
            leftLabel={progress.leftLabel}
            rightLabel={progress.rightLabel}
            marcos={progress.marcos}
          />
        )}

        {/* A vigência vive DENTRO do corpo, não numa faixa separada embaixo:
            a faixa criava uma terceira zona no card e empurrava o conteúdo
            útil pra cima. Aqui ela é mais uma linha do card. */}
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" style={{ color: ACCENT }} />
            <span>Vigência: {vigencia}</span>
          </div>
          <ArrowUpRight
            className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            style={{ color: ACCENT }}
          />
        </div>
      </div>
    </Link>
  );
};

// Paleta dos níveis (faixas), na ordem: bronze → prata → ouro → topo.
const NIVEL_CORES = [
  { bg: "#F8C6A8", fg: "#7C3A12" },
  { bg: "#D5D9DE", fg: "#3F4854" },
  { bg: "#F8DC8A", fg: "#7A5B04" },
  { bg: "#C7E9CE", fg: "#1F5B34" },
];

export const nivelCor = (idx: number) => NIVEL_CORES[Math.min(idx, NIVEL_CORES.length - 1)];

/**
 * Card de um nível/faixa da campanha. Atingido = destravado (sem cadeado);
 * o primeiro não atingido leva o selo "Próximo nível".
 */
export const NivelCard = ({
  titulo,
  ordem,
  atingido,
  proximo,
  condicoes,
  beneficios,
}: {
  titulo: string;
  ordem: number;
  atingido: boolean;
  proximo: boolean;
  condicoes: React.ReactNode[];
  beneficios: React.ReactNode[];
}) => {
  const cor = nivelCor(ordem);
  const Marker = ({ ok }: { ok: boolean }) =>
    ok ? (
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: ACCENT }} />
    ) : (
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    );

  return (
    <div className="flex flex-col overflow-hidden rounded-md">
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        style={{ backgroundColor: cor.bg, color: cor.fg }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Medal className="h-4 w-4 shrink-0" />
          <span className="truncate text-sm font-semibold">{titulo}</span>
        </div>
        {proximo ? (
          <span className="shrink-0 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
            Próximo nível
          </span>
        ) : atingido ? (
          <CheckCircle2 className="h-4 w-4 shrink-0" />
        ) : (
          <Lock className="h-4 w-4 shrink-0" />
        )}
      </div>

      <div className="flex-1 space-y-5 bg-[#F1F3F5] px-5 py-5 text-sm">
        <div className="space-y-2">
          <p className="font-semibold">Condições</p>
          <ul className="space-y-2">
            {condicoes.map((c, i) => (
              <li key={i} className="flex gap-2 text-muted-foreground">
                <Marker ok={atingido} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-2">
          <p className="font-semibold">Benefícios</p>
          <ul className="space-y-2">
            {beneficios.map((b, i) => (
              <li key={i} className="flex gap-2 text-muted-foreground">
                <Marker ok={atingido} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/** Banner do topo do detalhe: ícone + nome (com o olhinho ao lado), período e chamada da campanha. */
export const CampanhaHero = ({
  name,
  icon,
  periodo,
  description,
}: {
  name: string;
  icon?: string | null;
  periodo: string;
  description?: string | null;
}) => {
  const Icon = campaignIcon(icon);
  return (
    <div className="cabecalho-premium relative overflow-hidden rounded-[14px] bg-premium px-6 py-5 text-white">
      <span
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full border border-ouro/35 shadow-[0_0_0_40px_rgba(196,173,142,0.05)]"
      />
      <div className="relative flex items-center gap-3">
        <Icon className="h-6 w-6 text-ouro" />
        <span className="text-[24px] font-bold leading-tight tracking-[-0.02em]">{name}</span>
        <BotaoOlhinho className="-ml-1.5" />
      </div>
      <p className="relative mt-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-ouro">{periodo}</p>
      {description && <p className="relative mt-2 text-sm text-white/72">{description}</p>}
    </div>
  );
};

/**
 * Painel cinza de indicadores (o "Seus indicadores" da referência): rótulo em
 * cima, valor grande embaixo, primeiro item à esquerda e último à direita.
 * O valor passa pelo olhinho; o rótulo, não.
 */
export const IndicadoresPanel = ({
  items,
}: {
  items: Array<{ label: string; value: string; tone?: "default" | "good" | "bad" }>;
}) => {
  const m = useMascara();
  return (
  <div className="flex flex-wrap items-start justify-between gap-6 rounded-[12px] border border-border border-t-[3px] border-t-ouro bg-card px-6 py-5 sm:[&>*:last-child]:text-right">
    {items.map((it) => (
      <div key={it.label}>
        <p className="text-sm text-muted-foreground">{it.label}</p>
        <p
          className={
            "mt-0.5 text-xl font-bold " +
            (it.tone === "good"
              ? "text-ok"
              : it.tone === "bad"
                ? "text-red-700 dark:text-red-400"
                : "")
          }
        >
          {m(it.value)}
        </p>
      </div>
    ))}
  </div>
  );
};

/**
 * Palavra-chave do título no azul da marca — o realce que o design de
 * referência usa em "Seus *indicadores*", "*Informações* do programa" etc.
 */
export const Realce = ({ children }: { children: React.ReactNode }) => (
  <span style={{ color: ACCENT }}>{children}</span>
);

/** Cabeçalho de seção no padrão "Seus indicadores" / "Informações do programa". */
export const SecaoTitulo = ({
  children,
  subtitulo,
}: {
  children: React.ReactNode;
  subtitulo?: React.ReactNode;
}) => (
  <div className="space-y-1">
    <h2 className="text-lg font-semibold tracking-[-0.01em] text-marinho">{children}</h2>
    {subtitulo && <p className="text-sm text-muted-foreground">{subtitulo}</p>}
  </div>
);
